import type { CarePlan, ServiceRequest } from '@medplum/fhirtypes';
import React, { useMemo } from 'react';

import { Icon, Stack, Text, cn } from '@eluve/components';
import { formatFhirDate } from '@eluve/fhir-renderer';

import { cadenceRows, projectedVisits } from '../plan-list';

/**
 * The visit-schedule half of the Care Plans section (Rev7): the projected
 * visit timeline (plan-shapes' ladder — encounters-on-plan is out of scope,
 * so every visit is a projection, the next one leading) beside the
 * cadence-by-phase table from the plan's session-frequency snapshot.
 * Renders nothing when the plan carries no cadence.
 */
export type VisitScheduleCardProps = {
  plan: CarePlan;
  serviceRequests: ServiceRequest[];
  todayIsoDate: string;
};

const SHOWN_VISITS = 5;

export const VisitScheduleCard: React.FC<VisitScheduleCardProps> = ({
  plan,
  serviceRequests,
  todayIsoDate,
}) => {
  const cadence = useMemo(() => cadenceRows(plan), [plan]);
  const visits = useMemo(
    () =>
      projectedVisits(plan, serviceRequests, todayIsoDate)?.slice(
        0,
        SHOWN_VISITS,
      ),
    [plan, serviceRequests, todayIsoDate],
  );
  if (!cadence && !visits) return null;

  return (
    <section
      data-testid="visit-schedule-card"
      className="rounded-lg border border-border-primary bg-bg-primary"
    >
      <Stack
        direction="horizontal"
        gap={2}
        align="center"
        justify="between"
        className="h-12 px-4"
      >
        <Text inline size="sm" weight="semibold">
          Visit schedule
        </Text>
        <Icon
          name="CalendarClock"
          size="sm"
          className="text-text-tertiary opacity-60"
        />
      </Stack>
      <div className="flex flex-wrap gap-6 border-t border-border-primary p-4">
        {visits && (
          <div className="min-w-56 flex-1" data-testid="visit-timeline">
            {visits.map((visit, index) => (
              <div key={visit.number} className="flex items-center gap-3 py-1">
                <span
                  aria-hidden
                  className={cn(
                    'size-1.75 shrink-0 rounded-full',
                    index === 0 ? 'bg-text-insights' : 'bg-border-primary',
                  )}
                />
                <Text
                  inline
                  size="xs"
                  color="tertiary"
                  className="w-24 shrink-0 tabular-nums"
                >
                  {formatFhirDate(visit.date) ?? '—'}
                </Text>
                <Text
                  inline
                  size="sm"
                  weight="medium"
                  className={cn(index === 0 && 'text-text-insights')}
                >
                  {visit.gate ? 'Reassessment' : 'Visit'}
                  {index === 0 && ' · next'}
                </Text>
              </div>
            ))}
          </div>
        )}
        {cadence && (
          <div className="min-w-48 flex-1" data-testid="cadence-table">
            {cadence.map((row) => (
              <Stack
                key={row.weeksLabel}
                direction="horizontal"
                gap={4}
                align="center"
                justify="between"
                className="py-1"
              >
                <Text inline size="xs" color="tertiary">
                  {row.weeksLabel}
                </Text>
                <Text inline size="sm" weight="medium">
                  {row.cadence}
                </Text>
              </Stack>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};
